import React from "react";
import { protectPercent } from "../../utils/typography";

export type RichTextProps = {
    content: string | undefined;
    className?: string;
};

/**
 * RichText component that renders HTML content safely.
 * Supports basic HTML tags: <p>, <br>, <strong>, <em>, <mark>, <span>
 * Also converts markdown bold (**text**), italic (*text*) and links ([text](url)) to HTML.
 *
 * Erzeugte Links tragen einen Pfeil, wie `SectionTextLink` — die Schreibweise der
 * Seite fuer «das fuehrt woandershin». Er sitzt in einem eigenen
 * `aria-hidden`-Span: Vorgelesen waere «Offerten-Generator Rechtspfeil» nur
 * Laerm, gesehen traegt die Form die Aussage.
 *
 * Usage:
 * <RichText content="This is <strong>bold</strong> and <em>italic</em> text." />
 * <RichText content="This is **bold** and *italic* in markdown." />
 * <RichText content="Line 1<br />Line 2" />
 * <RichText content="This is <mark>highlighted</mark> text." />
 */
export const RichText: React.FC<RichTextProps> = ({ content, className = "" }) => {
    if (!content) {
        return null;
    }

    // Convert markdown bold (**text**), italic (*text*) and links ([text](url)) to HTML
    const processedContent = protectPercent(
        content
            /*
             * Links ZUERST (11.9.2026): Eine URL kann `*` enthalten, und die
             * Kursiv-Regel wuerde sie mitten im `href` zerschneiden.
             *
             * Der Fall, gegen den das steht: `[Offerten-Generator](/angebote/ki-anwendungen/…)`
             * erschien wortwoertlich auf /angebote/ki-anwendungen/potenzial — eckige Klammern,
             * runde Klammern, Pfad. Wer `**fett**` schreiben darf, erwartet, dass auch
             * die Link-Schreibweise traegt.
             *
             * Erlaubt sind interne Pfade, In-Page-Anker (`#abschnitt`) und http(s).
             * `javascript:` und `data:` bleiben Text,
             * sonst waere jedes Content-Feld ein Einfallstor — der Inhalt geht
             * anschliessend durch `dangerouslySetInnerHTML`.
             */
            .replace(
                /\[([^\]]+)\]\((\/[^)\s]*|#[^)\s]+|https?:\/\/[^)\s]+)\)/g,
                "<a href='$2'>$1<span aria-hidden='true'> \u2192</span></a>"
            )
            // Bold: **text** -> <strong>text</strong>
            .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
            // Italic: *text* -> <em>text</em> (but not if already part of bold **)
            .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>$1</em>")
    );

    // Links im Fliesstext sichtbar machen. Ohne das erben sie die Textfarbe und
    // sind von gewoehnlichem Text nicht zu unterscheiden — technisch klickbar,
    // praktisch unsichtbar (gemessen auf der Stellenanzeige: Linkfarbe und
    // Textfarbe beide rgb(39,39,39), keine Unterstreichung).
    //
    // `currentColor` statt einer festen Farbe, weil RichText auch auf dunklem
    // Grund steht (Kontakt-Sektion, `variant="dark"`). Eine Markenfarbe waere
    // dort unlesbar; die Unterstreichung traegt die Erkennbarkeit, der Hover
    // gibt die Rueckmeldung.
    const linkStyles =
        "[&_a]:underline [&_a]:underline-offset-2 [&_a]:decoration-current/40 " +
        "hover:[&_a]:decoration-current [&_a]:transition-colors";

    return (
        <span className={`${linkStyles} ${className}`.trim()} dangerouslySetInnerHTML={{ __html: processedContent }} />
    );
};

export default RichText;
